"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {NeoCard, NeoHeader} from "@/components/shared";
import {FirebaseContext} from "@/context/firebase/firebase.context";
import {useNeoContext} from "@/context/neo/neo.context";
import {ReportMenu} from "@/data";
import type {NextPage} from "next";
import {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import {BsFuelPump} from "react-icons/bs";
import {MdDirectionsBus} from "react-icons/md";
import {PiStudentBold} from "react-icons/pi";
import {TbRouteSquare} from "react-icons/tb";

import {twMerge} from "tailwind-merge";
const reportMenu = ReportMenu as REPORTMEUSCONFIG;

const BusIcon = ({className}: {className?: string}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className ?? "lucide lucide-bus"}
  >
    <path d="M8 6v6" />
    <path d="M15 6v6" />
    <path d="M2 12h19.6" />
    <path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3" />
    <circle cx="7" cy="18" r="2" />
    <path d="M9 18h5" />
    <circle cx="16" cy="18" r="2" />
  </svg>
);

const iconBase = {
  width: "20",
  height: "20",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const
};

const BarChartIcon = () => (
  <svg {...iconBase}>
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

const ScanIcon = () => (
  <svg {...iconBase}>
    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    <path d="M7 12h10" />
  </svg>
);

const UsersIcon = () => (
  <svg {...iconBase}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ShieldIcon = () => (
  <svg {...iconBase}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
  </svg>
);

const RouteIcon = () => <TbRouteSquare className="h-5 w-5" />;

const AttendanceIcon = () => <PiStudentBold className="h-5 w-5" />;

const VehicleIcon = () => <MdDirectionsBus className="h-5 w-5" />;

const FuelIcon = () => <BsFuelPump className="h-5 w-5" />;

const ListIcon = () => (
  <svg {...iconBase}>
    <path d="M8 6h13M8 12h13M8 18h13" />
    <path d="M3 6h.01M3 12h.01M3 18h.01" />
  </svg>
);

type CategoryStyle = {
  label: string;
  icon: React.FC;
};

const CATEGORY_META: Record<string, CategoryStyle> = {
  "Performance Reports": {label: "Performance", icon: BarChartIcon},
  "Bus Attendance Reports": {label: "Attendance", icon: AttendanceIcon},
  "RFID Reports": {label: "RFID", icon: ScanIcon},
  "Campus Attendance Reports": {label: "Campus", icon: UsersIcon},
  "Log Reports": {label: "Route", icon: RouteIcon},
  "Alert Reports": {label: "Safety", icon: ShieldIcon},
  "Hardware Status Reports": {label: "Vehicle", icon: VehicleIcon},
  "Fuel Reports": {label: "Fuel", icon: FuelIcon}
};

const getCategoryStyle = (header: string): CategoryStyle =>
  CATEGORY_META[header] ?? {
    label: header.replace(/ Reports$/, ""),
    icon: ListIcon
  };

export const Reports: NextPage = function () {
  const [modalOpen, setModalOpen] = useState(false);
  const [menuData, setMenuData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionType, setActionType] = useState("report");
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [highlightedCard, setHighlightedCard] = useState<string | null>(null);
  const [hasScrollMap, setHasScrollMap] = useState<Record<string, boolean>>({});
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const firebase = useContext(FirebaseContext);
  const {filterContext} = useNeoContext();

  const handleJumpToCard = (header: string) => {
    setExpandedCard(header);
    setHighlightedCard(header);
    cardRefs.current[header]?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
    clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedCard(prev => (prev === header ? null : prev));
    }, 1500);
  };

  useEffect(() => {
    if (menuData?.activeReport) {
      setModalOpen(true);
    }
  }, [menuData]);

  useLayoutEffect(() => {
    const map: Record<string, boolean> = {};
    Object.entries(cardRefs.current).forEach(([key, el]) => {
      if (el) {
        map[key] = el.scrollHeight > el.clientHeight + 1;
      }
    });
    setHasScrollMap(map);
  }, [searchTerm, filterContext?.client?.customerType]);

  const openPopup = (reportType: any, activereport: any) => {
    //reportType.activeReport = activereport;
    //setModalOpen(true);
    if (activereport.menu === "Fuel Analytics") {
      setActionType("fuelanalytics");
      const updatedReport = {...reportType, activeReport: activereport};
      setMenuData(updatedReport);
    } else if (activereport.menu === "Fuel Consumption") {
      setActionType("fuelconsumption");
      const updatedReport = {...reportType, activeReport: activereport};
      setMenuData(updatedReport);
    } else if (activereport.tableCardView) {
      setActionType("tablecard");
      const updatedReport = {...reportType, activeReport: activereport};
      setMenuData(updatedReport);
    } else {
      setActionType("report");
      const updatedReport = {...reportType, activeReport: activereport};
      setMenuData(updatedReport);
    }
  };

  const handleSearchChange = (e: any) => {
    setSearchTerm(e.target.value.toLowerCase().trim());
  };

  // **Headings for the quick-jump buttons**
  const visibleHeaders = reportMenu
    .filter(submenus => {
      let filteredMenus = submenus.menus.filter(
        menus =>
          searchTerm === "" || menus.menu.toLowerCase().includes(searchTerm)
      );
      filteredMenus = filteredMenus.filter(menus =>
        menus.acceptCustomerType?.includes(filterContext?.client?.customerType)
      );
      return filteredMenus.length > 0;
    })
    .map(submenus => submenus.header);

  // **Filter Report Menu Based on Search Term**
  const filteredReports = reportMenu
    .map(submenus => {
      // **Apply search filter**
      let filteredMenus = submenus.menus.filter(
        menus =>
          searchTerm === "" || menus.menu.toLowerCase().includes(searchTerm)
      );
      filteredMenus = filteredMenus.filter(menus =>
        menus.acceptCustomerType?.includes(filterContext?.client?.customerType)
      );
      // **Skip if no reports match the search term**
      if (filteredMenus.length === 0) {
        return null;
      }

      const cardKey = submenus.header;
      const hasScroll = hasScrollMap[cardKey] !== false;
      const isExpanded = hasScroll && expandedCard === cardKey;

      return (
        <div
          className={twMerge("relative", !isExpanded && "h-96 min-h-96")}
          key={cardKey}
        >
          <NeoCard
            ref={el => {
              cardRefs.current[cardKey] = el as HTMLDivElement | null;
            }}
            className={twMerge(
              "min-h-96 scroll-mt-48 overflow-y-auto p-4",
              hasScroll && "transition-all duration-300 ease-in-out",
              isExpanded ? "z-20 max-h-[90vh] shadow-xl" : "h-96",
              highlightedCard === cardKey &&
                "ring-1 ring-yellow-400 ring-offset-2"
            )}
            onWheel={() => {
              if (!hasScroll || expandedCard === cardKey) return;
              setExpandedCard(cardKey);
              cardRefs.current[cardKey]?.scrollIntoView({
                behavior: "smooth",
                block: "start"
              });
            }}
            onMouseLeave={() => setExpandedCard(null)}
          >
            <h2 className="mb-2 text-xl font-semibold">{submenus.header}</h2>
            <ul>
              {filteredMenus.map((menus, key) =>
                menus.url ? (
                  <li
                    key={menus.url + "_" + key}
                    className={twMerge(
                      "mb-1 flex min-h-16 cursor-pointer items-center justify-between transition-colors duration-200 hover:bg-gray-100",
                      key % 2 !== 0 ? "bg-gray-100" : "bg-white"
                    )}
                    onClick={() => {
                      openPopup(submenus, menus);
                      firebase?.reportAnalyticsLog(menus.menu);
                    }}
                    title={menus.menu_description}
                  >
                    <div className="flex w-full items-center gap-2 p-4">
                      <div className="rounded-full bg-yellow-300 p-2">
                        <BusIcon />
                      </div>
                      <div className="flex w-full flex-col gap-1">
                        <span className="text-sm font-semibold">
                          {menus.menu}
                        </span>
                        <p className="text-xs text-gray-600">
                          {menus.menu_description}
                        </p>
                      </div>
                    </div>
                  </li>
                ) : null
              )}
            </ul>
          </NeoCard>
        </div>
      );
    })
    .filter(Boolean); // **Removes empty results from the array**

  return (
    <div className="flex min-h-[calc(100vh-63px)] w-full flex-col px-10 py-10">
      <div className="mb-2 flex-row px-3">
        <NeoHeader message="Reports" />
      </div>

      {/* 🔍 Search Box */}
      <div className="p-4">
        <input
          type="text"
          placeholder="Search..."
          className="w-full rounded border px-2 py-1"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      {/* 🚀 Quick-jump buttons - hidden for now */}
      {/* {visibleHeaders.length > 0 && (
        <div className="sticky top-14 z-30 mx-4 rounded-xl border border-gray-300 bg-white p-1 shadow-md">
          <div className="flex gap-2  bg-white p-2 ">
            {visibleHeaders.map(header => {
              const {label, icon: Icon} = getCategoryStyle(header);
              const isActive = expandedCard === header;
              return (
                <button
                  key={header}
                  type="button"
                  onClick={() => handleJumpToCard(header)}
                  className={twMerge(
                    "my-1 flex flex-1 flex-col items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 py-1 text-black transition-colors",
                    isActive && "border-amber-500 bg-amber-50 text-amber-500"
                  )}
                >
                  <Icon />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )} */}

      {/* 📄 Reports Section */}
      <div className="flex-row">
        <div className="p-3">
          <div className="grid grid-cols-2 gap-2 p-1">
            {filteredReports.length > 0 ? (
              filteredReports
            ) : (
              <p className="text-gray-600">No reports found</p>
            )}
          </div>
        </div>
      </div>

      {/* 📌 Modal Popup */}
      <NeoModal
        onOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        actionType={actionType}
        hasActions={false}
        data={menuData}
      />
    </div>
  );
};
